import { RRule, type Weekday } from 'rrule';
import moment from 'utils/dayjs';

export type Day =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

export type RecurrenceOptions = {
  type: 'daily' | 'weekly' | 'monthly';
  everyNumberOf?: number;
  weekDays?: Array<Day>;
  weekDay?: Day;
  nthDayOfMonth?: number;
  dayOfMonthRange?: 'nthDayOfMonth' | 'nthWeekDayOfMonth';
  dayRange?: 'days' | 'weekdays';
  rangeEndType?: 'date' | 'count';
  weekNumber?: number;
  endDate?: Date;
  count?: number;
};

// RRule exposes its weekdays as two-letter statics (RRule.MO, RRule.TU, ...).
const weekdays: Record<Day, Weekday> = {
  monday: RRule.MO,
  tuesday: RRule.TU,
  wednesday: RRule.WE,
  thursday: RRule.TH,
  friday: RRule.FR,
  saturday: RRule.SA,
  sunday: RRule.SU,
};

const getWeekday = (day: Day) => weekdays[day];

// Force the date to UTC; RRule requires UTC dates
// https://github.com/jakubroztocil/rrule#timezone-support
const fixDate = (date: Date) =>
  moment.utc(moment(date).format('YYYY-MM-DD')).toDate();

const monToFri: Day[] = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
];

const recurrenceRuleBuilderMain = (
  date: Date,
  {
    type = 'daily',
    weekDays = [],
    weekNumber,
    weekDay = 'monday',
    everyNumberOf: interval = 1,
    nthDayOfMonth: monthDay,
    dayOfMonthRange,
    dayRange,
    rangeEndType = 'count',
    endDate = date,
    count = 1,
  }: Partial<RecurrenceOptions>
): RRule =>
  new RRule({
    dtstart: fixDate(date),
    ...(type === 'daily'
      ? {
          freq: RRule.DAILY,
          ...(dayRange === 'weekdays'
            ? { byweekday: monToFri.map(getWeekday), interval: 1 }
            : { interval }),
        }
      : {}),
    ...(type === 'weekly'
      ? { freq: RRule.WEEKLY, interval, byweekday: weekDays.map(getWeekday) }
      : {}),
    ...(type === 'monthly'
      ? {
          interval: 1,
          freq: RRule.MONTHLY,
          ...(dayOfMonthRange === 'nthDayOfMonth'
            ? {
                // For 29th or later, use closest available day
                // https://github.com/jakubroztocil/rrule/issues/114
                bysetpos: -1,
                bymonthday: Array.from({
                  length: Math.max(1, Number(monthDay) - 27),
                }).map((_, i) => Number(monthDay) - i),
              }
            : { byweekday: getWeekday(weekDay).nth(weekNumber!) }),
        }
      : {}),
    ...(rangeEndType === 'date' ? { until: fixDate(endDate) } : { count }),
  });

const numericFields = ['everyNumberOf', 'nthDayOfMonth', 'weekNumber', 'count'];

const numberOrUndefined = (value: unknown) => {
  const number = Number(value);
  return Number.isNaN(number) || value === '' ? undefined : number;
};

const fixRecurrenceOptions = (
  recurrenceOptions: Partial<RecurrenceOptions>
): Partial<RecurrenceOptions> =>
  Object.keys(recurrenceOptions)
    .map(key => ({
      [key]: numericFields.includes(key)
        ? numberOrUndefined(recurrenceOptions[key as keyof RecurrenceOptions])
        : recurrenceOptions[key as keyof RecurrenceOptions],
    }))
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

const recurrenceRuleBuilder = (
  date: Date,
  recurrenceOptions: Partial<RecurrenceOptions>
): RRule =>
  recurrenceRuleBuilderMain(date, fixRecurrenceOptions(recurrenceOptions));

export const nextOccurrence = (
  date: Date,
  recurrenceOptions: Partial<RecurrenceOptions>
): Date => recurrenceRuleBuilder(date, recurrenceOptions).after(fixDate(date))!;

export default recurrenceRuleBuilder;
